import { useState, useEffect, useRef, useCallback, memo } from "react";
import { useAuth, API } from "../App";
import { useTicketsStore } from "../store";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { EmptyTickets } from "../components/EmptyStates";
import { format, parseISO } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Ticket,
  Plane,
  Train,
  Bus,
  Ship,
  Loader2,
  Plus,
  FileText,
  Trash2,
  Pencil,
  X,
  ExternalLink,
  Camera,
  Wallet,
  ScanLine,
  Compass,
} from "lucide-react";
import BarcodeScanner from "../components/BarcodeScanner";
import DriveFilePicker from "../components/DriveFilePicker";
import { EmailImportButton } from "../components/EmailImport";
import { useAuthStore } from "../store";

// Moved TicketForm outside of the component to prevent re-renders
const TicketForm = memo(({ 
  ticket, 
  onChange, 
  onSubmit, 
  isEdit = false, 
  fileRef,
  cameraInputRef,
  handleFileUpload,
  uploading,
  saving,
  onScanClick,
  onDriveClick,
  trips = []
}) => (
  <div className="space-y-4 mt-4">
    {/* Quick Actions: Scan, Photo & Drive */}
    <div className="flex gap-2">
      <input
        type="file"
        ref={cameraInputRef}
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileUpload(e, isEdit)}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex-1 glass-input justify-center border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs sm:text-sm h-9"
        onClick={onScanClick}
        data-testid="scan-barcode-btn"
      >
        <ScanLine className="w-3.5 h-3.5 mr-1.5" />
        <span className="text-xs">Scan</span>
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex-1 glass-input justify-center border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs sm:text-sm h-9"
        onClick={() => cameraInputRef?.current?.click()}
        disabled={uploading}
        data-testid="camera-btn"
      >
        <Camera className="w-3.5 h-3.5 mr-1.5" />
        <span className="text-xs">Photo</span>
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex-1 glass-input justify-center border-sky-500/30 text-sky-400 hover:bg-sky-500/10 text-xs sm:text-sm h-9"
        onClick={onDriveClick}
        data-testid="drive-import-btn"
        title="Import from Google Drive"
      >
        <svg className="w-3.5 h-3.5 mr-1.5" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
          <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
          <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
          <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
          <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
          <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
          <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
        </svg>
        <span className="text-xs">Drive</span>
      </Button>
    </div>

    <div>
      <Label className="text-zinc-400">Type</Label>
      <Select
        value={ticket.type}
        onValueChange={(value) => onChange("type", value)}
      >
        <SelectTrigger className="glass-input">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="glass-card border-white/10">
          <SelectItem value="flight">Flight</SelectItem>
          <SelectItem value="train">Train</SelectItem>
          <SelectItem value="bus">Bus</SelectItem>
          <SelectItem value="ferry">Ferry</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {/* Trip Selector */}
    {trips.length > 0 && (
      <div>
        <Label className="text-zinc-400">Link to Trip (Optional)</Label>
        <Select
          value={ticket.trip_id || "none"}
          onValueChange={(value) => onChange("trip_id", value === "none" ? null : value)}
        >
          <SelectTrigger className="glass-input">
            <SelectValue placeholder="Select a trip..." />
          </SelectTrigger>
          <SelectContent className="glass-card border-white/10">
            <SelectItem value="none">No trip</SelectItem>
            {trips.map((trip) => (
              <SelectItem key={trip.trip_id} value={trip.trip_id}>
                {trip.name} ({trip.destination_country})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )}

    <div>
      <Label className="text-zinc-400">Provider/Airline *</Label>
      <Input
        className="glass-input"
        placeholder="e.g., Lufthansa, SNCF, FlixBus"
        value={ticket.provider}
        onChange={(e) => onChange("provider", e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-testid="ticket-provider-input"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-zinc-400">From *</Label>
        <Input
          className="glass-input"
          placeholder="City/Station"
          value={ticket.departure_location}
          onChange={(e) => onChange("departure_location", e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-testid="ticket-from-input"
        />
      </div>
      <div>
        <Label className="text-zinc-400">To *</Label>
        <Input
          className="glass-input"
          placeholder="City/Station"
          value={ticket.arrival_location}
          onChange={(e) => onChange("arrival_location", e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-testid="ticket-to-input"
        />
      </div>
    </div>

    <div>
      <Label className="text-zinc-400">Departure Date & Time</Label>
      <Input
        type="datetime-local"
        className="glass-input"
        value={ticket.departure_time}
        onChange={(e) => onChange("departure_time", e.target.value)}
      />
    </div>

    <div>
      <Label className="text-zinc-400">Booking Reference</Label>
      <Input
        className="glass-input"
        placeholder="e.g., ABC123"
        value={ticket.booking_reference}
        onChange={(e) => onChange("booking_reference", e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-zinc-400">Price</Label>
        <Input
          type="number"
          step="0.01"
          className="glass-input"
          placeholder=""
          value={ticket.price}
          onChange={(e) => onChange("price", e.target.value)}
        />
      </div>
      <div>
        <Label className="text-zinc-400">Currency</Label>
        <Select
          value={ticket.currency}
          onValueChange={(value) => onChange("currency", value)}
        >
          <SelectTrigger className="glass-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="glass-card border-white/10">
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
            <SelectItem value="CHF">CHF</SelectItem>
            <SelectItem value="JPY">JPY</SelectItem>
            <SelectItem value="AUD">AUD</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    {/* Budget Integration */}
    {ticket.price && (
      <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-emerald-400" />
            <span className="text-white text-sm">Add to My Budget</span>
          </div>
          <Switch
            checked={ticket.add_to_budget}
            onCheckedChange={(checked) => onChange("add_to_budget", checked)}
          />
        </div>
        {ticket.add_to_budget && (
          <Select
            value={ticket.budget_category}
            onValueChange={(value) => onChange("budget_category", value)}
          >
            <SelectTrigger className="glass-input">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent className="glass-card border-white/10">
              <SelectItem value="flights">Flights</SelectItem>
              <SelectItem value="trains_buses">Train & Buses</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    )}

    {/* Attached File Display */}
    {ticket.file_name && (
      <div>
        <Label className="text-zinc-400">Attached File</Label>
        <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
          <FileText className="w-4 h-4 text-emerald-400" />
          <a 
            href={ticket.file_url?.startsWith('http') ? ticket.file_url : `${process.env.REACT_APP_BACKEND_URL}${ticket.file_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-emerald-300 truncate flex-1 hover:text-emerald-200 hover:underline cursor-pointer"
            title="Click to view/download"
          >
            {ticket.file_name}
          </a>
          <button
            type="button"
            onClick={() => { onChange("file_url", ""); onChange("file_name", ""); }}
            className="p-1 hover:bg-red-500/20 rounded"
          >
            <X className="w-3 h-3 text-red-400" />
          </button>
        </div>
      </div>
    )}
    
    {/* Hidden file input for camera (used by top buttons) */}
    <input
      type="file"
      ref={fileRef}
      className="hidden"
      accept="image/*,.pdf"
      onChange={(e) => handleFileUpload(e, isEdit)}
    />

    <div>
      <Label className="text-zinc-400">Notes</Label>
      <Input
        className="glass-input"
        placeholder="Additional information"
        value={ticket.notes}
        onChange={(e) => onChange("notes", e.target.value)}
        autoComplete="off"
      />
    </div>

    <Button
      className="w-full btn-primary"
      onClick={onSubmit}
      disabled={saving}
      data-testid={isEdit ? "update-ticket-btn" : "save-ticket-btn"}
    >
      {saving ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : null}
      {isEdit ? "Update Ticket" : "Save Ticket"}
    </Button>
  </div>
));

TicketForm.displayName = "TicketForm";

const MyTickets = () => {
  const { user } = useAuth();
  
  // Use Zustand store for tickets
  const { 
    tickets, 
    setTickets, 
    addTicket: storeAddTicket,
    updateTicket: storeUpdateTicket,
    removeTicket: storeRemoveTicket,
  } = useTicketsStore();
  
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const editCameraInputRef = useRef(null);
  
  const emptyTicket = {
    type: "flight",
    provider: "",
    departure_location: "",
    arrival_location: "",
    departure_time: "",
    booking_reference: "",
    price: "",
    currency: "EUR",
    notes: "",
    file_url: "",
    file_name: "",
    add_to_budget: false,
    budget_category: "flights",
    trip_id: null,
  };
  
  const [newTicket, setNewTicket] = useState(emptyTicket);
  const [editTicket, setEditTicket] = useState(emptyTicket);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await axios.get(`${API}/tickets`, { withCredentials: true });
        setTickets(response.data);
      } catch (error) {
        setTickets([]);
      } finally {
        setLoading(false);
      }
      try {
        const response = await axios.get(`${API}/trips`, { withCredentials: true });
        setTrips(response.data);
      } catch (error) {
        /* non-critical */
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = useCallback(async (event, isEdit = false) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload an image (JPG, PNG, WebP) or PDF file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fileUrl = uploadResponse.data.url;
      if (isEdit) {
        setEditTicket(prev => ({ ...prev, file_url: fileUrl, file_name: file.name }));
      } else {
        setNewTicket(prev => ({ ...prev, file_url: fileUrl, file_name: file.name }));
      }
      toast.success("File attached");
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  }, []);

  const addToBudget = async (ticketData, ticketId) => {
    if (!ticketData.add_to_budget || !ticketData.price) return;
    
    try {
      await axios.post(`${API}/budget/expenses`, {
        category: ticketData.budget_category || "flights",
        description: `${ticketData.type}: ${ticketData.departure_location} → ${ticketData.arrival_location}`,
        amount: parseFloat(ticketData.price),
        currency: ticketData.currency,
        date: ticketData.departure_time ? ticketData.departure_time.split("T")[0] : new Date().toISOString().split("T")[0],
        source_id: ticketId,
        source_type: "ticket",
      }, {
        withCredentials: true,
      });
      toast.success("Added to budget!");
    } catch (error) {
    }
  };

  const handleAddTicket = async () => {
    if (!newTicket.provider || !newTicket.departure_location || !newTicket.arrival_location) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const ticketData = {
        ...newTicket,
        departure_time: newTicket.departure_time ? `${newTicket.departure_time}:00` : null,
        price: newTicket.price ? parseFloat(newTicket.price) : null,
      };
      
      const response = await axios.post(`${API}/tickets`, ticketData, {
        withCredentials: true,
      });
      
      // Add to budget if requested (pass the ticket_id from the response)
      if (newTicket.add_to_budget && newTicket.price) {
        await addToBudget(newTicket, response.data.ticket_id);
      }
      
      setTickets([response.data, ...tickets]);
      toast.success("Ticket added successfully");
      setShowAddDialog(false);
      setNewTicket(emptyTicket);
    } catch (error) {
      toast.error("Failed to add ticket");
    } finally {
      setSaving(false);
    }
  };

  const handleEditTicket = async () => {
    if (!editTicket.provider || !editTicket.departure_location || !editTicket.arrival_location) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const ticketData = {
        ...editTicket,
        price: editTicket.price ? parseFloat(editTicket.price) : null,
      };
      
      const response = await axios.put(`${API}/tickets/${selectedTicket.ticket_id}`, ticketData, {
        withCredentials: true,
      });
      setTickets(tickets.map(t => t.ticket_id === selectedTicket.ticket_id ? response.data : t));
      toast.success("Ticket updated successfully");
      setShowEditDialog(false);
      setSelectedTicket(null);
    } catch (error) {
      toast.error("Failed to update ticket");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) return;
    
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, {
        withCredentials: true,
      });
      setTickets(tickets.filter((t) => t.ticket_id !== ticketId));
      toast.success("Ticket deleted");
    } catch (error) {
      toast.error("Failed to delete ticket");
    }
  };

  const openEditDialog = (ticket) => {
    setSelectedTicket(ticket);
    setEditTicket({
      type: ticket.type || "flight",
      provider: ticket.provider || "",
      departure_location: ticket.departure_location || "",
      arrival_location: ticket.arrival_location || "",
      departure_time: ticket.departure_time ? ticket.departure_time.slice(0, 16) : "",
      booking_reference: ticket.booking_reference || "",
      price: ticket.price?.toString() || "",
      currency: ticket.currency || "EUR",
      notes: ticket.notes || "",
      file_url: ticket.file_url || "",
      file_name: ticket.file_name || "",
      add_to_budget: false,
      budget_category: "flights",
      trip_id: ticket.trip_id || null,
    });
    setShowEditDialog(true);
  };

  const openViewDialog = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewDialog(true);
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "flight": return <Plane className="w-5 h-5" />;
      case "train": return <Train className="w-5 h-5" />;
      case "bus": return <Bus className="w-5 h-5" />;
      case "ferry": return <Ship className="w-5 h-5" />;
      default: return <Ticket className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "flight": return "text-sky-400 bg-sky-500/20";
      case "train": return "text-emerald-400 bg-emerald-500/20";
      case "bus": return "text-amber-400 bg-amber-500/20";
      case "ferry": return "text-blue-400 bg-blue-500/20";
      default: return "text-violet-400 bg-violet-500/20";
    }
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "Not set";
    try {
      return format(parseISO(dateTimeStr), "MMM d, yyyy 'at' HH:mm");
    } catch {
      return dateTimeStr;
    }
  };

  // Use useCallback to prevent re-creating handlers on every render
  const handleNewTicketChange = useCallback((field, value) => {
    setNewTicket(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditTicketChange = useCallback((field, value) => {
    setEditTicket(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle barcode scan result
  const handleBarcodeScan = useCallback((scanData) => {
    if (scanData.type === "boarding_pass" && scanData.parsed) {
      const parsed = scanData.parsed;
      setNewTicket(prev => ({
        ...prev,
        type: "flight",
        provider: parsed.provider || prev.provider,
        departure_location: parsed.departure_location || prev.departure_location,
        arrival_location: parsed.arrival_location || prev.arrival_location,
        booking_reference: parsed.booking_reference || prev.booking_reference,
        notes: parsed.seat ? `Seat: ${parsed.seat}` : prev.notes,
      }));
      toast.success("Boarding pass scanned! Form filled with ticket data.");
    } else if (scanData.type === "booking_reference" && scanData.parsed?.booking_reference) {
      setNewTicket(prev => ({
        ...prev,
        booking_reference: scanData.parsed.booking_reference,
      }));
      toast.success("Booking reference scanned!");
    } else if (scanData.type === "url") {
      setNewTicket(prev => ({
        ...prev,
        notes: `Scanned URL: ${scanData.parsed.url}`,
      }));
      toast.success("URL scanned and added to notes.");
    } else {
      // Generic barcode - add to notes
      setNewTicket(prev => ({
        ...prev,
        notes: prev.notes ? `${prev.notes}\nBarcode: ${scanData.raw}` : `Barcode: ${scanData.raw}`,
      }));
      toast.success("Barcode scanned and added to notes.");
    }
  }, []);

  const openScanner = useCallback(() => {
    setShowScanner(true);
  }, []);

  const openDrivePicker = useCallback(() => {
    setShowDrivePicker(true);
  }, []);

  const handleDriveFileSelect = useCallback(async ({ url, fileName }) => {
    // File already uploaded by DriveFilePicker, just update the ticket state
    setNewTicket(prev => ({
      ...prev,
      file_url: url,
      file_name: fileName,
    }));
  }, []);

  const importTicketFromEmail = useCallback(async (ticket) => {
    const ticketData = {
      type: ticket.type || "flight",
      provider: ticket.provider || "Unknown",
      booking_reference: ticket.booking_reference || null,
      departure_location: ticket.departure_location || "Unknown",
      arrival_location: ticket.arrival_location || "Unknown",
      departure_time: ticket.departure_time || null,
      arrival_time: ticket.arrival_time || null,
      price: ticket.price != null ? parseFloat(ticket.price) : null,
      currency: ticket.currency || "EUR",
      notes: ticket.notes || null,
    };
    const response = await axios.post(`${API}/tickets`, ticketData, { withCredentials: true });
    storeAddTicket(response.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container p-4 pt-6 pb-28" data-testid="my-tickets">
      <SEO title="My Tickets" description="Organize your flight, train, and event tickets. Scan barcodes and import from email." path="/tickets" />
      <div className="max-w-4xl mx-auto">
      {/* Hero Banner */}
      <div className="relative h-36 sm:h-44 lg:h-52 overflow-hidden -mx-4 -mt-6 mb-6 rounded-b-2xl">
        <img src="https://images.unsplash.com/photo-1540339832862-474599807836?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-5 flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">My Tickets</h1>
            <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Flights, trains & events</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary text-sm" data-testid="add-ticket-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Ticket
                </Button>
              </DialogTrigger>
          <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Ticket</DialogTitle>
            </DialogHeader>
            <TicketForm 
              ticket={newTicket} 
              onChange={handleNewTicketChange}
              onSubmit={handleAddTicket}
              fileRef={fileInputRef}
              cameraInputRef={cameraInputRef}
              handleFileUpload={handleFileUpload}
              uploading={uploading}
              saving={saving}
              onScanClick={openScanner}
              onDriveClick={openDrivePicker}
              trips={trips}
            />
          </DialogContent>
        </Dialog>
          <EmailImportButton
            mode="ticket"
            onImportTickets={importTicketFromEmail}
          />
          </div>
        </div>
      </div>

      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Boarding Pass / Ticket"
      />

      {/* Google Drive File Picker */}
      <DriveFilePicker
        isOpen={showDrivePicker}
        onClose={() => setShowDrivePicker(false)}
        onFileSelect={handleDriveFileSelect}
        acceptedTypes={['application/pdf', 'image/']}
        title="Import Ticket from Google Drive"
      />

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Ticket</DialogTitle>
          </DialogHeader>
          <TicketForm 
            ticket={editTicket} 
            onChange={handleEditTicketChange}
            onSubmit={handleEditTicket}
            isEdit={true}
            fileRef={editFileInputRef}
            cameraInputRef={editCameraInputRef}
            handleFileUpload={handleFileUpload}
            uploading={uploading}
            saving={saving}
            onScanClick={openScanner}
            onDriveClick={openDrivePicker}
            trips={trips}
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedTicket && getTypeIcon(selectedTicket.type)}
              Ticket Details
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-500 text-xs">Type</Label>
                  <p className="text-white capitalize">{selectedTicket.type}</p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-xs">Provider</Label>
                  <p className="text-white">{selectedTicket.provider}</p>
                </div>
              </div>
              
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-zinc-500 text-xs">From</Label>
                    <p className="text-white font-medium">{selectedTicket.departure_location}</p>
                  </div>
                  <div className="text-zinc-600 text-xl">→</div>
                  <div className="text-right">
                    <Label className="text-zinc-500 text-xs">To</Label>
                    <p className="text-white font-medium">{selectedTicket.arrival_location}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-500 text-xs">Departure</Label>
                  <p className="text-white">{formatDateTime(selectedTicket.departure_time)}</p>
                </div>
                {selectedTicket.booking_reference && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Reference</Label>
                    <p className="text-white font-mono">{selectedTicket.booking_reference}</p>
                  </div>
                )}
              </div>

              {selectedTicket.price && (
                <div>
                  <Label className="text-zinc-500 text-xs">Price</Label>
                  <p className="text-sky-400 font-medium text-lg">
                    {selectedTicket.currency} {selectedTicket.price}
                  </p>
                </div>
              )}

              {selectedTicket.notes && (
                <div>
                  <Label className="text-zinc-500 text-xs">Notes</Label>
                  <p className="text-zinc-300">{selectedTicket.notes}</p>
                </div>
              )}

              {selectedTicket.file_name && (
                <div>
                  <Label className="text-zinc-500 text-xs">Attached File</Label>
                  <div className="mt-1 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center justify-between">
                    <a 
                      href={selectedTicket.file_url?.startsWith('http') ? selectedTicket.file_url : `${process.env.REACT_APP_BACKEND_URL}${selectedTicket.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 flex-1 hover:opacity-80"
                    >
                      <FileText className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-300 hover:underline">{selectedTicket.file_name}</span>
                    </a>
                    <a
                      href={selectedTicket.file_url?.startsWith('http') ? selectedTicket.file_url : `${process.env.REACT_APP_BACKEND_URL}${selectedTicket.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-emerald-500/20 rounded-lg transition-colors"
                      title="Open file"
                    >
                      <ExternalLink className="w-4 h-4 text-emerald-400" />
                    </a>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 btn-outline"
                  onClick={() => {
                    setShowViewDialog(false);
                    openEditDialog(selectedTicket);
                  }}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 btn-outline text-red-400 border-red-500/30 hover:bg-red-500/10"
                  onClick={() => {
                    setShowViewDialog(false);
                    handleDeleteTicket(selectedTicket.ticket_id);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {tickets.length === 0 ? (
        <EmptyTickets onAction={() => setShowAddDialog(true)} />
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <div
              key={ticket.ticket_id}
              className="glass-card p-4 hover:border-sky-500/30 transition-all cursor-pointer"
              onClick={() => openViewDialog(ticket)}
              data-testid={`ticket-card-${ticket.ticket_id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${getTypeColor(ticket.type)}`}>
                  {getTypeIcon(ticket.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-white">{ticket.provider}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-zinc-500">
                        {ticket.type}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-zinc-300">
                    <div className="flex items-center gap-2">
                      <span>{ticket.departure_location}</span>
                      <span className="text-zinc-600">→</span>
                      <span>{ticket.arrival_location}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm text-zinc-500">
                    {ticket.departure_time && (
                      <span>{formatDateTime(ticket.departure_time)}</span>
                    )}
                    {ticket.price && (
                      <span className="text-sky-400 font-medium">
                        {ticket.currency} {ticket.price}
                      </span>
                    )}
                  </div>
                  {ticket.booking_reference && (
                    <div className="mt-2">
                      <span className="text-xs text-zinc-500">
                        Ref: {ticket.booking_reference}
                      </span>
                    </div>
                  )}
                  {ticket.trip_id && trips.find(t => t.trip_id === ticket.trip_id) && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-violet-400">
                      <Compass className="w-3 h-3" />
                      <span>{trips.find(t => t.trip_id === ticket.trip_id)?.name}</span>
                    </div>
                  )}
                  {ticket.file_name && (
                    <a 
                      href={ticket.file_url?.startsWith('http') ? ticket.file_url : `${process.env.REACT_APP_BACKEND_URL}${ticket.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300 hover:underline"
                    >
                      <FileText className="w-3 h-3" />
                      <span>{ticket.file_name}</span>
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(ticket);
                    }}
                    className="p-2 hover:bg-sky-500/20 rounded transition-colors"
                    data-testid={`edit-ticket-${ticket.ticket_id}`}
                  >
                    <Pencil className="w-4 h-4 text-sky-400" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTicket(ticket.ticket_id);
                    }}
                    className="p-2 hover:bg-red-500/20 rounded transition-colors"
                    data-testid={`delete-ticket-${ticket.ticket_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default MyTickets;
